import { createFileRoute } from "@tanstack/react-router";
import { Globe } from "lucide-react";
import { StatewideServiceHub } from "@/components/StatewideServiceHub";
import { buildCityCards } from "@/lib/statewideServiceCities";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildServiceHubScripts } from "@/lib/serviceHubSchema";

const CANONICAL = "https://coloradowebimpressions.com/web-design";
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({ service: "web-design" })}`;

export const Route = createFileRoute("/web-design")({
  head: () => ({
    meta: [
      { title: "Colorado Web Design — Websites That Help Businesses Grow" },
      {
        name: "description",
        content:
          "Custom Colorado web design — Denver, Springs, Boulder, Fort Collins, Pueblo & more. Sub-2-sec mobile, owner-led since 2006. Free quote in 24 hrs.",
      },
      { property: "og:title", content: "Colorado Web Design — Websites That Help Businesses Grow" },
      { property: "og:description", content: "Mobile-first, conversion-focused Colorado websites — helping Colorado businesses succeed online since 2006." },
      { property: "og:url", content: CANONICAL },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Web Design That Helps You Grow",
        description: "Websites that help Colorado businesses succeed online.",
      }),
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: buildServiceHubScripts({
      path: "/web-design",
      name: "Web Design",
      description: "Custom, conversion-focused web design for Colorado businesses across Denver, Colorado Springs, Boulder, Fort Collins, Pueblo and the Front Range.",
      serviceType: "Web Design",
      priceRange: "$$",
      faqs: [
        { q: "How much does a custom website cost in Colorado?", a: "Website packages are $1,850 (4 pages), $2,750 (10 pages) and $5,950 (up to 50 pages), each with one year of hosting, SSL and a traffic dashboard. Extra pages are $85. Compare them on our pricing page." },
        { q: "How long does it take to build a new website?", a: "Most website packages are typically delivered in under 7 business days once we have your content." },
        { q: "Do I own my website after you build it?", a: "Yes. You own the design, the code, the domain, the hosting login, and the analytics. No proprietary platforms, no hostage strategies." },
        { q: "Is the site mobile-friendly and fast?", a: "Every site is built mobile-first, scores 90+ on Core Web Vitals, and loads under 2 seconds on a 4G connection. Site speed is part of our SEO foundation." },
      ],
    }),
  }),
  component: () => (
    <StatewideServiceHub
      service="Web Design"
      serviceLower="web design"
      eyebrow="Colorado Web Design"
      headline="Websites that help Colorado businesses succeed online"
      goldWord="succeed online"
      subheadline="Sub-2-second mobile loads, sticky click-to-call, on-page SEO baked in. Built to turn visitors into calls and quote requests for businesses anywhere in Colorado."
      benefits={[
        "Mobile-first, sub-2-second loads on real 4G",
        "Custom-designed — never a template",
        "Local SEO + schema baked in from day one",
        "Owner-led design, in-house dev, no outsourcing",
        "Transparent published pricing — no quote games",
      ]}
      benefitIcon={Globe}
      servicesGrid
      industriesGrid
      cities={buildCityCards("web-design", "/colorado-springs-web-design")}
      pickerHeading="Proudly serving businesses across Colorado"
      closing="Building outside our listed cities? We design websites for businesses across the entire state — get in touch and we'll scope it."
    />
  ),
});
